import { useState, useEffect } from 'react'
import LandingPage from './components/LandingPage'
import PreferenceSelector from './components/PreferenceSelector' // Fallback
import BookingForm from './components/BookingForm'
import ShareLink from './components/ShareLink'
import ResponseForm from './components/ResponseForm' // Fallback
import ResultsDisplay from './components/ResultsDisplay' // Fallback
import Success from './pages/Success'
import Cancel from './pages/Cancel'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'
import CookiePolicy from './pages/CookiePolicy'
import About from './pages/About'
import Dashboard from './pages/Dashboard'
import DashboardSimple from './pages/DashboardSimple'
import Footer from './components/Footer'
import Navigation from './components/Navigation'
import AuthModal from './components/AuthModal'
import { createParticles } from './utils/particles'
import { getCardData } from './services/linkService'
import { loadTopic } from './data/topics/index.js'
import { getCurrentUser, checkPremiumStatus, onAuthChange } from './services/firebaseService'

function App() {
  const [showLanding, setShowLanding] = useState(true)
  const [currentStep, setCurrentStep] = useState('preferences')
  const [selectedPreferences, setSelectedPreferences] = useState([])
  const [bookingData, setBookingData] = useState(null)
  const [cardData, setCardData] = useState(null)
  const [responseData, setResponseData] = useState(null)
  const [cardShortCode, setCardShortCode] = useState(null) // For status tracking
  const [useV2, setUseV2] = useState(true) // Try V2 first, fallback to V1 if needed
  const [showAuth, setShowAuth] = useState(false)
  const [user, setUser] = useState(null)
  const [hasPremium, setHasPremium] = useState(false)

  // Initialize theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('parkort_theme') || 'dark'
    document.documentElement.setAttribute('data-theme', savedTheme)
  }, [])

  useEffect(() => {
    createParticles()
    
    // Check for legal pages routes
    const pathname = window.location.pathname
    const legalPages = {
      '/privacy-policy': 'privacy',
      '/dist/privacy-policy': 'privacy',
      '/terms-of-service': 'terms',
      '/dist/terms-of-service': 'terms',
      '/cookie-policy': 'cookie',
      '/dist/cookie-policy': 'cookie',
      '/about': 'about',
      '/dist/about': 'about'
    }
    
    if (legalPages[pathname]) {
      setCurrentStep(legalPages[pathname])
      setShowLanding(false)
      return
    }
    
    // Redirect if someone comes to root with card/results params but app is in /dist/
    const urlParams = new URLSearchParams(window.location.search)
    const cardParam = urlParams.get('card')
    const resultsParam = urlParams.get('results')
    
    // If we're at root but have card/results params, redirect to /dist/
    if ((pathname === '/' || pathname === '') && (cardParam || resultsParam)) {
      const newPath = `/dist${window.location.pathname}${window.location.search}`
      window.location.replace(newPath)
      return
    }
    
    // Don't show landing if we have card/results params or short code
    if (cardParam || resultsParam || pathname.match(/\/(c|r)\//)) {
      setShowLanding(false)
    }
    
    const successParam = urlParams.get('success')
    const cancelParam = urlParams.get('cancel')
    const dashboardParam = urlParams.get('dashboard')
    const stepParam = urlParams.get('step')
    const shareParam = urlParams.get('share')
    const senderRespondedParam = urlParams.get('sender_responded')
    
    // If step=preferences in URL, go directly to preference selector
    if (stepParam === 'preferences') {
      setCurrentStep('preferences')
      setShowLanding(false)
      return
    }
    
    // If dashboard=true in URL, show dashboard
    if (dashboardParam === 'true') {
      setCurrentStep('dashboard')
      setShowLanding(false)
      return
    }
    
    // If share=true and sender has responded, restore share link state
    if (shareParam === 'true' && senderRespondedParam === 'true') {
      const storedPrefs = sessionStorage.getItem('parkort_sender_preferences')
      const storedBooking = sessionStorage.getItem('parkort_sender_booking')
      if (storedPrefs) {
        try {
          const prefs = JSON.parse(storedPrefs)
          const booking = storedBooking ? JSON.parse(storedBooking) : null
          setSelectedPreferences(prefs)
          setBookingData(booking)
          setCurrentStep('share')
          setShowLanding(false)
          // Clear URL params
          window.history.replaceState({}, '', window.location.pathname)
          return
        } catch (e) {
          console.error('Error restoring share state:', e)
        }
      }
    }
    
    // If success=true in URL, go directly to preferences (skip landing)
    if (successParam === 'true') {
      setShowLanding(false)
      setCurrentStep('preferences')
    }

    // Check for short code in URL (e.g., /c/abc123 or /dist/c/abc123)
    const pathMatch = window.location.pathname.match(/\/(c|r)\/([a-zA-Z0-9]+)$/)
    
    // Check for success/cancel pages (works from root or /dist/)
    // Only show Success page if it's explicitly /success path, not if it's just ?success=true (that's handled in PreferenceSelector)
    const isSuccessPage = (window.location.pathname === '/success' || 
                          window.location.pathname === '/dist/success') && 
                          !urlParams.get('topic_id') // Don't show Success page if we have topic_id (redirect back to preferences)
    const isCancelPage = window.location.pathname === '/cancel' || 
                        window.location.pathname === '/dist/cancel' || 
                        cancelParam
    
    if (isSuccessPage) {
      setCurrentStep('success')
      return
    }
    
    if (isCancelPage) {
      setCurrentStep('cancel')
      return
    }
    
    if (resultsParam) {
      // Old format: ?results=...
      try {
        const data = JSON.parse(decodeURIComponent(resultsParam))
        
        // Load topic data if preferences have topicId
        if (data.preferences && Array.isArray(data.preferences)) {
          const topicIds = [...new Set(data.preferences.map(p => p.topicId).filter(Boolean))]
          const topicDataMap = {}
          
          // Use async IIFE to handle await
          ;(async () => {
            for (const topicId of topicIds) {
              try {
                const topicData = await loadTopic(topicId)
                topicDataMap[topicId] = topicData
              } catch (e) {
                // Topic could not be loaded - continue with other topics
              }
            }
            
            data.topicData = topicDataMap
            setResponseData(data)
            setCurrentStep('results')
          })()
        } else {
          setResponseData(data)
          setCurrentStep('results')
        }
      } catch (e) {
        console.error('Error parsing results data:', e)
      }
    } else if (pathMatch) {
      // New format: /c/abc123 or /r/abc123
      const [, type, shortCode] = pathMatch
      loadCardFromShortCode(shortCode, type === 'r')
    } else if (cardParam) {
      // Old format: ?card=...
      try {
        const data = JSON.parse(decodeURIComponent(cardParam))
        setCardData(data)
        
        // Clear sender session storage when loading recipient card
        if (cardParam && !sessionStorage.getItem('parkort_sender_has_responded')) {
          sessionStorage.removeItem('parkort_is_sender_response')
          sessionStorage.removeItem('parkort_sender_responses')
        }
        
        // Check if this is sender responding to their own card
        const senderResponse = urlParams.get('sender_response') === 'true'
        if (senderResponse) {
          // Store that this is sender's response
          sessionStorage.setItem('parkort_is_sender_response', 'true')
        }
        
        setCurrentStep('response')
      } catch (e) {
        console.error('Error parsing card data:', e)
      }
    }
    
    // Check if sender needs to respond (from ShareLink)
    const senderNeedsToRespond = sessionStorage.getItem('parkort_sender_needs_to_respond')
    if (senderNeedsToRespond === 'true' && !cardParam && !pathMatch) {
      // Load stored preferences and show response form
      const storedPrefs = sessionStorage.getItem('parkort_sender_preferences')
      const storedBooking = sessionStorage.getItem('parkort_sender_booking')
      if (storedPrefs) {
        try {
          const prefs = JSON.parse(storedPrefs)
          const booking = storedBooking ? JSON.parse(storedBooking) : null
          setCardData({ preferences: prefs, booking })
          sessionStorage.setItem('parkort_is_sender_response', 'true')
          setCurrentStep('response')
          sessionStorage.removeItem('parkort_sender_needs_to_respond')
        } catch (e) {
          console.error('Error loading sender preferences:', e)
        }
      }
    }
    
    async function loadCardFromShortCode(shortCode, isResponse = false) {
      try {
        const data = await getCardData(shortCode)
        if (data) {
          // Load topic data for preferences if they have topicId
          if (data.preferences && Array.isArray(data.preferences)) {
            const topicIds = [...new Set(data.preferences.map(p => p.topicId).filter(Boolean))]
            const topicDataMap = {}
            
            for (const topicId of topicIds) {
              try {
                const topicData = await loadTopic(topicId)
                topicDataMap[topicId] = topicData
              } catch (e) {
                // Topic could not be loaded - continue with other topics
              }
            }
            
            data.topicData = topicDataMap
          }
          
          if (isResponse) {
            setResponseData(data)
            setCurrentStep('results')
          } else {
            setCardData(data)
            setCurrentStep('response')
          }
        } else {
          console.error('Card not found')
          setCurrentStep('preferences')
        }
      } catch (error) {
        console.error('Error loading card:', error)
        setCurrentStep('preferences')
      }
    }
  }, [])

  const handlePreferencesSelected = (preferences) => {
    setSelectedPreferences(preferences)
    setCurrentStep('booking')
  }

  const handleBookingComplete = (bookingData) => {
    // bookingData can be null, or an object with booking and/or deadline
    setBookingData(bookingData)
    // NYT FLOW: G� til senderResponse i stedet for share
    setCurrentStep('senderResponse')
  }

  const handleSenderResponseComplete = async (responseData) => {
    // Sender har svaret - gem svar og g� til share
    setResponseData(responseData)
    // Vi bruger lange links nu, s� vi beh�ver ikke shortCode
    setCurrentStep('share')
  }

  const handleLinkCreated = () => {
    // Link created, stay on share step
  }

  const handleResponseComplete = async (responseData) => {
    console.log('?? handleResponseComplete called, navigating to responseLink')
    console.log('responseData:', responseData)
    
    // Load topic data for results if needed
    if (responseData.responses && window.pendingCardData) {
      try {
        const cardData = JSON.parse(decodeURIComponent(window.pendingCardData))
        if (cardData.preferences) {
          const topicIds = [...new Set(cardData.preferences.map(p => p.topicId).filter(Boolean))]
          const topicDataMap = {}
          
          for (const topicId of topicIds) {
            try {
              const topicData = await loadTopic(topicId)
              topicDataMap[topicId] = topicData
            } catch (e) {
              console.warn(`Could not load topic ${topicId}:`, e)
            }
          }
          
          responseData.topicData = topicDataMap
          responseData.preferences = cardData.preferences
        }
      } catch (e) {
        console.error('Error loading topic data:', e)
      }
    }
    
    setResponseData(responseData)
    setCurrentStep('responseLink')
    console.log('? currentStep set to responseLink')
  }

  const handleStartOver = () => {
    setSelectedPreferences([])
    setBookingData(null)
    setCardData(null)
    setResponseData(null)
    setCurrentStep('preferences')
    window.history.replaceState({}, document.title, '/')
  }

  const handleGetStarted = () => {
    setShowLanding(false)
    setCurrentStep('preferences')
  }

  const handleAuthSuccess = (authenticatedUser) => {
    setUser(authenticatedUser)
    setShowAuth(false)
    checkPremiumStatus(authenticatedUser.uid).then(setHasPremium)
  }

  const handleNavigate = (step) => {
    setCurrentStep(step)
    setShowLanding(false)
  }

  return (
    <div className="app">
      <a href="#main-content" className="skip-to-main">
        Spring til hovedindhold
      </a>
      <Navigation 
        currentStep={currentStep} 
        onNavigate={handleNavigate}
        onAuthRequest={() => setShowAuth(true)}
        user={user}
        hasPremium={hasPremium}
        onUserChange={setUser}
        onPremiumChange={setHasPremium}
      />
      <div className="luxury-bg"></div>
      <div className="particles" id="particles"></div>
      
      <div className="container" id="main-content">
        {showLanding ? (
          <LandingPage onGetStarted={handleGetStarted} />
        ) : (
          <>
            {currentStep === 'preferences' && (
          useV2 ? (
            <PreferenceSelector 
              onComplete={handlePreferencesSelected}
              onError={() => {
                console.warn('V2 failed, falling back to V1')
                setUseV2(false)
              }}
            />
          ) : (
            <PreferenceSelector 
              onComplete={handlePreferencesSelected}
            />
          )
        )}
        
        {currentStep === 'booking' && (
          <BookingForm
            preferences={selectedPreferences}
            onComplete={handleBookingComplete}
            onBack={() => setCurrentStep('preferences')}
          />
        )}
        
        {currentStep === 'senderResponse' && (
          useV2 && selectedPreferences?.[0]?.topicId ? (
            <ResponseForm
              cardData={{
                preferences: selectedPreferences,
                booking: bookingData?.booking || (bookingData?.date ? bookingData : null)
              }}
              onComplete={handleSenderResponseComplete}
              isSenderResponse={true}
            />
          ) : (
            <ResponseForm
              cardData={{
                preferences: selectedPreferences,
                booking: bookingData?.booking || (bookingData?.date ? bookingData : null)
              }}
              onComplete={handleSenderResponseComplete}
              isSenderResponse={true}
            />
          )
        )}
        
        {currentStep === 'share' && (
          <>
            {console.log('?? Rendering ShareLink for SENDER (currentStep=share, isResponseLink=false)')}
            <ShareLink
              preferences={selectedPreferences}
              booking={bookingData?.booking || (bookingData?.date ? bookingData : null)}
              deadline={bookingData?.deadline}
              onComplete={handleLinkCreated}
              onStartOver={handleStartOver}
            />
          </>
        )}
        
        {currentStep === 'response' && cardData && (
          useV2 && cardData.preferences?.[0]?.topicId ? (
            <ResponseForm
              cardData={cardData}
              onComplete={handleResponseComplete}
              isSenderResponse={false}
            />
          ) : (
            <ResponseForm
              cardData={cardData}
              onComplete={handleResponseComplete}
              isSenderResponse={false}
            />
          )
        )}
        
        {currentStep === 'responseLink' && (
          <>
            {console.log('?? Rendering ShareLink for RECIPIENT (currentStep=responseLink, isResponseLink=true)')}
            <ShareLink
              preferences={cardData?.preferences || responseData?.preferences || []}
              booking={cardData?.booking || null}
              responseLink={responseData?.responseLink || window.responseLink || ''}
              onComplete={() => {}}
              onStartOver={handleStartOver}
              isResponseLink={true}
            />
          </>
        )}
        
        {currentStep === 'results' && responseData && (
          useV2 && responseData.topicData ? (
            <ResultsDisplay
              data={responseData}
              onStartOver={handleStartOver}
            />
          ) : (
            <ResultsDisplay
              data={responseData}
              onStartOver={handleStartOver}
            />
          )
        )}

        {currentStep === 'dashboard' && (
          <DashboardSimple />
        )}

        {currentStep === 'success' && (
          <Success />
        )}

        {currentStep === 'cancel' && (
          <Cancel />
        )}

        {currentStep === 'privacy' && (
          <PrivacyPolicy />
        )}

        {currentStep === 'terms' && (
          <TermsOfService />
        )}

        {currentStep === 'cookie' && (
          <CookiePolicy />
        )}

        {currentStep === 'about' && (
          <About />
        )}
          </>
        )}
      </div>

      {/* Auth Modal - managed at App level like PaywallPopup */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        mode="signin"
      />
      
      {/* Footer - show on landing and legal pages */}
      {(showLanding || ['privacy', 'terms', 'cookie', 'about'].includes(currentStep)) && (
        <Footer />
      )}
    </div>
  )
}

export default App
